import React from "react";
import styled from "styled-components";
import { FiArrowRight } from "react-icons/fi";
import {
  FiMonitor,
  FiSmartphone,
  FiDatabase,
  FiUsers,
  FiShoppingCart,
  FiLayers,
  FiCloud,
  FiCode,
  FiLayout,
  FiSettings,
  FiShield,
  FiServer,
  FiBarChart2,
  FiTrendingUp,
  FiBriefcase,
  FiCpu,
  FiFileText,
  FiMessageSquare,
  FiZap,
  FiGlobe,
} from "react-icons/fi";

/* ================= BRAND THEME ================= */

const BRAND = {
  gradient: "linear-gradient(135deg, #ff8a3d, #d55bff)",
  dark: "#16181d",
  text: "#242938",
  softText: "#636a7a",
  border: "rgba(255, 255, 255, 0.55)",
  shadow: "0 30px 80px rgba(17, 24, 39, 0.10)",
  shadowHover: "0 40px 100px rgba(213, 91, 255, 0.22)",
};

/* ================= SERVICES DATA (UNCHANGED) ================= */

const services = [
  {
    title: "Website Development",
    icon: FiMonitor,
    desc: "High-performance websites crafted for scalability, speed, and exceptional user experience.",
    points: [
      "Responsive UI Design",
      "Custom Web Applications",
      "SEO-Ready Architecture",
      "Fast Page Load Speed",
      "Cross-Browser Support",
      "Secure Code Practices",
    ],
  },
  {
    title: "Mobile App Development",
    icon: FiSmartphone,
    desc: "Native and cross-platform mobile applications built for performance and usability.",
    points: [
      "Android & iOS Apps",
      "Flutter & React Native",
      "Optimized Performance",
      "Offline Capabilities",
      "Secure API Integration",
      "App Store Deployment",
    ],
  },
  {
    title: "ERP Software Solutions",
    icon: FiDatabase,
    desc: "Enterprise-grade ERP systems that streamline and automate business operations.",
    points: [
      "Inventory Management",
      "HR & Payroll",
      "Finance Automation",
      "Role-Based Access",
      "Real-Time Reporting",
      "System Integration",
    ],
  },
  {
    title: "CRM Solutions",
    icon: FiUsers,
    desc: "Smart CRM platforms to manage customers, sales pipelines, and relationships.",
    points: [
      "Lead Management",
      "Sales Automation",
      "Customer Insights",
      "Email Integration",
      "Workflow Automation",
      "Data Security",
    ],
  },
  {
    title: "E-Commerce Development",
    icon: FiShoppingCart,
    desc: "Conversion-focused eCommerce solutions for scalable online businesses.",
    points: [
      "Shopify & Custom Stores",
      "Payment Gateway Setup",
      "Product Management",
      "Cart & Checkout Flow",
      "Order Tracking",
      "Scalable Architecture",
    ],
  },
  {
    title: "Web Portals",
    icon: FiLayers,
    desc: "Secure portals for employees, customers, and partners.",
    points: [
      "User Dashboards",
      "Admin Panels",
      "Role-Based Access",
      "Secure Authentication",
      "Data Visualization",
      "System Integration",
    ],
  },
  {
    title: "Cloud & SaaS Solutions",
    icon: FiCloud,
    desc: "Cloud-native SaaS platforms designed for scalability and performance.",
    points: [
      "AWS & Azure",
      "Cloud Migration",
      "Auto Scaling",
      "High Availability",
      "Data Backup",
      "Cost Optimization",
    ],
  },
  {
    title: "API Development",
    icon: FiCode,
    desc: "Robust APIs for seamless integration between systems.",
    points: [
      "REST & GraphQL",
      "Secure Authentication",
      "High Performance",
      "Third-Party Integration",
      "Scalable Design",
      "API Documentation",
    ],
  },
  {
    title: "UI / UX Design",
    icon: FiLayout,
    desc: "User-centered design that enhances usability and engagement.",
    points: [
      "User Research",
      "Wireframes & Prototypes",
      "Modern UI Trends",
      "Usability Testing",
      "Brand Consistency",
      "Conversion Optimization",
    ],
  },
  {
    title: "Software Maintenance",
    icon: FiSettings,
    desc: "Ongoing support and optimization to keep systems running smoothly.",
    points: [
      "Bug Fixing",
      "Performance Tuning",
      "Security Updates",
      "Version Upgrades",
      "Monitoring",
      "Technical Support",
    ],
  },
  {
    title: "Cyber Security Solutions",
    icon: FiShield,
    desc: "Advanced security solutions to protect data and systems.",
    points: [
      "Threat Monitoring",
      "Vulnerability Testing",
      "Data Encryption",
      "Compliance Standards",
      "Risk Management",
      "Security Audits",
    ],
  },
  {
    title: "Cloud Migration",
    icon: FiServer,
    desc: "Seamless migration of applications and data to cloud platforms.",
    points: [
      "Zero Downtime",
      "Secure Transfer",
      "Legacy Modernization",
      "Cloud Optimization",
      "Backup Strategy",
      "Post-Migration Support",
    ],
  },
  {
    title: "Business Analytics",
    icon: FiBarChart2,
    desc: "Data-driven insights to make informed business decisions.",
    points: [
      "Custom Dashboards",
      "Real-Time Reports",
      "Trend Analysis",
      "KPI Tracking",
      "Predictive Insights",
      "Decision Support",
    ],
  },
  {
    title: "Digital Marketing",
    icon: FiTrendingUp,
    desc: "Growth-focused digital marketing strategies.",
    points: [
      "SEO & SEM",
      "Social Media Marketing",
      "Google Ads",
      "Lead Generation",
      "Analytics Tracking",
      "Brand Growth",
    ],
  },
  {
    title: "Enterprise Applications",
    icon: FiBriefcase,
    desc: "Custom enterprise software solutions for large-scale organizations.",
    points: [
      "Scalable Architecture",
      "High Security",
      "System Integration",
      "Process Automation",
      "Custom Modules",
      "Enterprise Support",
    ],
  },
  {
    title: "Custom Software Development",
    icon: FiCpu,
    desc: "Tailor-made software solutions for unique business needs.",
    points: [
      "Requirement Analysis",
      "Agile Development",
      "High Performance",
      "Scalable Systems",
      "Secure Codebase",
      "Long-Term Support",
    ],
  },
  {
    title: "CMS Development",
    icon: FiFileText,
    desc: "Content management systems for easy website administration.",
    points: [
      "WordPress",
      "Headless CMS",
      "Custom Themes",
      "SEO Friendly",
      "Content Workflow",
      "Easy Maintenance",
    ],
  },
  {
    title: "Chatbot Development",
    icon: FiMessageSquare,
    desc: "AI-powered chatbots to automate customer interactions.",
    points: [
      "24/7 Support",
      "AI Conversations",
      "Multi-Platform",
      "CRM Integration",
      "Lead Qualification",
      "Analytics Tracking",
    ],
  },
  {
    title: "Automation Solutions",
    icon: FiZap,
    desc: "Intelligent automation to boost productivity and efficiency.",
    points: [
      "Workflow Automation",
      "Process Optimization",
      "AI Integration",
      "Error Reduction",
      "Time Saving",
      "Scalable Automation",
    ],
  },
  {
    title: "Global IT Consulting",
    icon: FiGlobe,
    desc: "Strategic IT consulting to accelerate business growth.",
    points: [
      "Technology Roadmaps",
      "Digital Transformation",
      "Cost Optimization",
      "System Architecture",
      "Risk Assessment",
      "Growth Strategy",
    ],
  },
];

const handleWhatsApp = (serviceTitle) => {
  const phone = "918072487998";
  const message = `I am interested to build a ${serviceTitle}.`;
  const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  window.open(url, "_blank");
};

/* ================= COMPONENT ================= */

const Services = () => {
  return (
    <Section id="services">
      <Container>
        <HeaderWrap>
          <Eyebrow>Technology Solutions</Eyebrow>
          <Title>Our IT Services</Title>
          <Subtitle>
            Empowering businesses with world-class technology solutions
          </Subtitle>
        </HeaderWrap>

        <Grid>
          {services.map((service, index) => {
            const Icon = service.icon;

            return (
              <Card key={index}>
                <TopRow>
                  <IconBox>
                    <Icon size={30} />
                  </IconBox>
                  <ServiceNumber>
                    {String(index + 1).padStart(2, "0")}
                  </ServiceNumber>
                </TopRow>

                <CardTitle>{service.title}</CardTitle>
                <CardDesc>{service.desc}</CardDesc>

                <PointsGrid>
                  <Column>
                    {service.points.slice(0, 3).map((point, i) => (
                      <PointItem key={i}>{point}</PointItem>
                    ))}
                  </Column>

                  <Column>
                    {service.points.slice(3, 6).map((point, i) => (
                      <PointItem key={i}>{point}</PointItem>
                    ))}
                  </Column>
                </PointsGrid>

                <CTAButton onClick={() => handleWhatsApp(service.title)}>
                  <span>Build With Us</span>
                  <FiArrowRight />
                </CTAButton>
              </Card>
            );
          })}
        </Grid>
      </Container>
    </Section>
  );
};

export default Services;

/* ================= STYLES ================= */

const Section = styled.section`
  position: relative;
  width: 100%;
  max-width: 100%;
  padding: 110px 0 100px;
  background:
    radial-gradient(circle at top left, rgba(255, 138, 61, 0.12), transparent 34%),
    radial-gradient(circle at top right, rgba(213, 91, 255, 0.14), transparent 28%),
    linear-gradient(180deg, #fff8f2 0%, #f8f4ff 45%, #f7f8fc 100%);
  overflow-x: hidden;
  box-sizing: border-box;

  @media (max-width: 992px) {
    padding: 90px 0 80px;
  }

  @media (max-width: 768px) {
    padding: 72px 0 64px;
  }

  @media (max-width: 480px) {
    padding: 60px 0 56px;
  }
`;

const Container = styled.div`
  width: min(1320px, calc(100% - 32px));
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 768px) {
    width: calc(100% - 20px);
  }
`;

const HeaderWrap = styled.div`
  width: 100%;
  max-width: 820px;
  min-width: 0;
  text-align: center;
  margin: 0 auto 56px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  padding: 9px 16px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(213, 91, 255, 0.18);
  color: #7c3aed;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  box-shadow: 0 10px 30px rgba(124, 58, 237, 0.08);
  margin-bottom: 18px;
  box-sizing: border-box;
  white-space: normal;
  text-align: center;

  @media (max-width: 480px) {
    font-size: 11px;
    padding: 8px 14px;
  }
`;

const Title = styled.h2`
  margin: 0 0 14px;
  font-size: clamp(34px, 5vw, 56px);
  line-height: 1.08;
  font-weight: 900;
  letter-spacing: -0.03em;
  background: ${BRAND.gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  word-break: break-word;
  overflow-wrap: anywhere;

  @media (max-width: 768px) {
    line-height: 1.12;
  }
`;

const Subtitle = styled.p`
  margin: 0 auto;
  max-width: 720px;
  font-size: 16px;
  line-height: 1.8;
  color: ${BRAND.softText};
  word-break: break-word;
  overflow-wrap: anywhere;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.75;
    max-width: 100%;
    padding: 0 4px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 24px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 18px;
  }
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  padding: 28px;
  border-radius: 28px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.82));
  border: 1px solid ${BRAND.border};
  backdrop-filter: blur(18px);
  box-shadow: ${BRAND.shadow};
  overflow: hidden;
  box-sizing: border-box;
  transition: transform 0.38s ease, box-shadow 0.38s ease, border-color 0.38s ease;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at top right, rgba(213, 91, 255, 0.12), transparent 28%),
      radial-gradient(circle at top left, rgba(255, 138, 61, 0.12), transparent 26%);
    opacity: 0;
    transition: opacity 0.38s ease;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-10px);
    box-shadow: ${BRAND.shadowHover};
    border-color: rgba(213, 91, 255, 0.25);
  }

  &:hover::before {
    opacity: 1;
  }

  @media (max-width: 768px) {
    padding: 22px 18px 20px;
    border-radius: 22px;
  }

  @media (max-width: 480px) {
    padding: 20px 16px 18px;
  }
`;

const TopRow = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 20px;
  width: 100%;
  min-width: 0;
`;

const IconBox = styled.div`
  width: 66px;
  height: 66px;
  flex-shrink: 0;
  border-radius: 20px;
  background: ${BRAND.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 18px 40px rgba(213, 91, 255, 0.28);

  @media (max-width: 768px) {
    width: 58px;
    height: 58px;
    border-radius: 18px;
  }

  @media (max-width: 480px) {
    width: 54px;
    height: 54px;
    border-radius: 16px;
  }
`;

const ServiceNumber = styled.span`
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #8b5cf6;
  background: rgba(139, 92, 246, 0.08);
  border: 1px solid rgba(139, 92, 246, 0.14);
  padding: 10px 12px;
  border-radius: 999px;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 480px) {
    font-size: 12px;
    padding: 8px 10px;
  }
`;

const CardTitle = styled.h3`
  position: relative;
  z-index: 1;
  margin: 0 0 10px;
  color: ${BRAND.text};
  font-size: 22px;
  line-height: 1.3;
  font-weight: 800;
  letter-spacing: -0.02em;
  word-break: break-word;
  overflow-wrap: anywhere;
  min-width: 0;

  @media (max-width: 768px) {
    font-size: 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const CardDesc = styled.p`
  position: relative;
  z-index: 1;
  margin: 0 0 22px;
  color: ${BRAND.softText};
  font-size: 14px;
  line-height: 1.8;
  min-height: 78px;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;

  @media (max-width: 1180px) {
    min-height: auto;
  }

  @media (max-width: 768px) {
    font-size: 13.5px;
    line-height: 1.75;
    margin-bottom: 18px;
  }
`;

const PointsGrid = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 20px;
  margin-bottom: 24px;
  padding: 18px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.62);
  border: 1px solid rgba(99, 102, 241, 0.08);
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 10px;
    padding: 16px 14px;
    border-radius: 18px;
  }
`;

const Column = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  min-width: 0;
`;

const PointItem = styled.li`
  position: relative;
  padding-left: 22px;
  margin-bottom: 11px;
  color: #4b5563;
  font-size: 13px;
  line-height: 1.6;
  word-break: break-word;
  overflow-wrap: anywhere;
  min-width: 0;

  &:last-child {
    margin-bottom: 0;
  }

  &::before {
    content: "✔";
    position: absolute;
    left: 0;
    top: 0;
    color: #ff8a3d;
    font-size: 12px;
    font-weight: 700;
  }

  @media (max-width: 480px) {
    font-size: 12.8px;
  }
`;

const CTAButton = styled.button`
  position: relative;
  z-index: 1;
  margin-top: auto;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  max-width: 100%;
  padding: 13px 22px;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  background: ${BRAND.gradient};
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  box-shadow: 0 14px 36px rgba(213, 91, 255, 0.34);
  transition: transform 0.32s ease, box-shadow 0.32s ease, opacity 0.32s ease;
  box-sizing: border-box;
  white-space: nowrap;

  svg {
    font-size: 18px;
    transition: transform 0.28s ease;
    flex-shrink: 0;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 20px 44px rgba(213, 91, 255, 0.42);

    svg {
      transform: translateX(4px);
    }
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
    min-height: 46px;
    padding: 12px 18px;
    font-size: 13px;
  }
`;